<script setup lang="ts">
/**
 * Vector (SVG/DXF) export panel — brief 01-export.md Mode 3's "Copy Selection
 * UI". The main copy is drawn solid and always included; ghost copies are
 * tappable to add to the export (an inline SVG stand-in for "the tile canvas
 * shown with ghost copies", chosen over reusing the live TileCanvas component
 * so this stays a simple, self-contained picker rather than a second canvas
 * renderer with its own zoom/pan state).
 */
import { computed, ref } from 'vue';
import { IconChevronLeft } from '@tabler/icons-vue';
import { apply } from '@/geometry/affine';
import { usePatternStore } from '@/stores/pattern';
import { useLayersStore } from '@/stores/layers';
import { exportVector, vectorCopyCandidates, type VectorCopyCandidate } from '@/services/vectorExport';

const emit = defineEmits<{ back: []; done: [] }>();

const patternStore = usePatternStore();
const layers = useLayersStore();

const candidates = ref<VectorCopyCandidate[]>(vectorCopyCandidates());
const mainKey = computed(() => candidates.value.find((c) => c.isMain)?.key);
const selected = ref<Set<string>>(new Set(mainKey.value ? [mainKey.value] : []));

function toggle(c: VectorCopyCandidate): void {
  if (c.isMain) return; // always included
  const next = new Set(selected.value);
  if (next.has(c.key)) next.delete(c.key);
  else next.add(c.key);
  selected.value = next;
}

const hasTraceContent = computed(() => layers.traceLayers.some((l) => l.visible && l.d));

function polygonPoints(matrix: VectorCopyCandidate['matrix']): string {
  return patternStore.currentPattern
    .polygon()
    .map(([x, y]) => apply(matrix, [x, y]))
    .map(([x, y]) => `${x.toFixed(4)},${y.toFixed(4)}`)
    .join(' ');
}

const viewBox = computed(() => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const c of candidates.value) {
    for (const [x, y] of patternStore.currentPattern.polygon().map(([px, py]) => apply(c.matrix, [px, py]))) {
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
  }
  const pad = Math.max(maxX - minX, maxY - minY, 1) * 0.08;
  return `${minX - pad} ${minY - pad} ${maxX - minX + 2 * pad} ${maxY - minY + 2 * pad}`;
});

const selectedCopies = computed(() => candidates.value.filter((c) => selected.value.has(c.key)));

function doExport(): void {
  exportVector(selectedCopies.value);
  emit('done');
}
</script>

<template>
  <div class="panel">
    <button class="back-link" @click="emit('back')">
      <IconChevronLeft :size="15" stroke-width="2" />
      Back
    </button>

    <p v-if="!hasTraceContent" class="empty">
      No traced vector shapes yet — use the Trace tool to draw an outline before exporting
      vector copies.
    </p>

    <template v-else>
      <p class="lead">Tap the ghost copies you need — the main copy is always included.</p>

      <svg class="picker" :viewBox="viewBox" preserveAspectRatio="xMidYMid meet">
        <polygon
          v-for="c in candidates"
          :key="c.key"
          :points="polygonPoints(c.matrix)"
          :class="{ main: c.isMain, selected: selected.has(c.key) && !c.isMain }"
          class="copy"
          @click="toggle(c)"
        />
      </svg>

      <p class="count">{{ selectedCopies.length }} copy{{ selectedCopies.length === 1 ? '' : 'ies' }} selected</p>

      <button class="primary-btn" @click="doExport">Export SVG + DXF</button>
    </template>
  </div>
</template>

<style scoped>
.panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  align-self: flex-start;
  background: transparent;
  border: none;
  color: var(--text-muted);
  font-size: 12.5px;
  cursor: pointer;
  padding: 0;
}

.back-link:hover {
  color: var(--text-primary);
}

.empty,
.lead {
  margin: 0;
  font-size: 13px;
  color: var(--text-muted);
  line-height: 1.5;
}

.picker {
  width: 100%;
  height: 260px;
  background: var(--canvas-bg);
  border: 1px solid var(--ui-border);
  border-radius: 10px;
}

.copy {
  fill: color-mix(in srgb, var(--accent) 8%, transparent);
  stroke: var(--ui-border);
  stroke-width: 0.01;
  cursor: pointer;
}

.copy:hover {
  stroke: var(--accent);
}

.copy.selected {
  fill: color-mix(in srgb, var(--accent) 35%, transparent);
  stroke: var(--accent);
}

.copy.main {
  fill: color-mix(in srgb, var(--accent) 55%, transparent);
  stroke: var(--accent);
  cursor: default;
}

.count {
  margin: 0;
  font-size: 12px;
  color: var(--text-muted);
}

.primary-btn {
  min-height: 42px;
  background: var(--accent);
  color: var(--accent-contrast);
  font-size: 14px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}
</style>
